import React from 'react';
import { Link } from 'react-router-dom';
import { Award, BookOpen, CalendarClock, ClipboardCheck } from 'lucide-react';
import { useLms } from '../../context/LmsContext';
import { LearnerCourseCard, LearnerProgramCard } from '../../components/CourseCard';
import { ButtonLink, Card, MetricCard, PageHeader, StatusBadge } from '../../components/ui';
import { derivedAssignmentStatus, getAssignmentProgress, getProgramProgress, isOverdue } from '../../utils/lmsSelectors';

export default function LearnerDashboard() {
  const { data, currentUser } = useLms();
  const assignments = data.assignments.filter((assignment) => assignment.assignedToUserId === currentUser.id && assignment.status !== 'revoked');
  const programAssignments = (data.programAssignments || []).filter((assignment) => assignment.assignedToUserId === currentUser.id && assignment.status !== 'revoked');
  const completed = assignments.filter((assignment) => assignment.status === 'completed').length;
  const overdue = assignments.filter(isOverdue).length;
  const avgProgress = assignments.length ? Math.round(assignments.reduce((sum, assignment) => sum + getAssignmentProgress(data, assignment).percent, 0) / assignments.length) : 0;
  const nextAssignments = assignments.filter((assignment) => assignment.status !== 'completed').slice(0, 3);

  return (
    <>
      <PageHeader title="Learner dashboard" eyebrow="My training" description="Continue assigned courses, track due dates, view grades, and download certificates." />
      <div className="metrics-grid">
        <MetricCard label="Assigned courses" value={assignments.length} helper="Required and optional" icon={<BookOpen size={22} />} />
        <MetricCard label="Completed" value={completed} helper="Training finished" icon={<ClipboardCheck size={22} />} />
        <MetricCard label="Average progress" value={`${avgProgress}%`} helper="Across active courses" icon={<CalendarClock size={22} />} />
        <MetricCard label="Programs" value={programAssignments.length} helper="Bundled course paths" icon={<Award size={22} />} />
      </div>
      {overdue > 0 && <Card className="warning-card"><strong>{overdue} overdue training item(s)</strong><p>Open My Courses and complete overdue training as soon as possible.</p></Card>}
      {programAssignments.length > 0 && (
        <div className="course-grid">
          {programAssignments.filter((assignment) => getProgramProgress(data, assignment).status !== 'completed').slice(0, 2).map((assignment) => <LearnerProgramCard key={assignment.id} data={data} programAssignment={assignment} />)}
        </div>
      )}
      <div className="course-grid">
        {nextAssignments.map((assignment) => <LearnerCourseCard key={assignment.id} data={data} assignment={assignment} />)}
      </div>
      <Card>
        <div className="card-row between"><h2>Training status summary</h2><ButtonLink to="/learner/my-courses" variant="secondary">View all courses</ButtonLink></div>
        {assignments.map((assignment) => {
          const course = data.courses.find((item) => item.id === assignment.courseId);
          return (
            <div key={assignment.id} className="stacked-row">
              <Link className="text-link strong-link" to={`/learner/course/${assignment.id}`}>{course?.title}</Link>
              <StatusBadge status={derivedAssignmentStatus(data, assignment)} />
            </div>
          );
        })}
      </Card>
    </>
  );
}
